import * as React from "react";
import { DateRangePickerProps, DateRangePicker as MyDateRange, Range, defaultStaticRanges, defaultInputRanges, createStaticRanges } from "react-date-range";

import { Box, Button, Menu } from "@mui/material";

import { subWeeks, startOfYear, endOfYear, subYears } from "date-fns";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./style.css";

import calendar from "./../../assets/icons/calendar.svg";

const allStaticRanges = [
  ...defaultStaticRanges,
  ...createStaticRanges([
    {
      label: 'This Year',
      range: () => ({ startDate: startOfYear(new Date()), endDate: new Date() }),
    },
    {
      label: 'Last Year',
      range: () => ({
        startDate: startOfYear(subYears(new Date(), 1)),
        endDate: endOfYear(subYears(new Date(), 1)),
      }),
    },
  ]),
];

export interface DatePickerProps extends DateRangePickerProps {
  onDateSelect: (value: Range) => void;
  startDate: Date;
  endDate: Date;
};

const formatDate = (dateString: Date) => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  };

  return date.toLocaleDateString(undefined, options);
};

const DateRangePicker = ({ startDate, endDate, onDateSelect, ...others }: DatePickerProps) => {
  const changeRange = (item: Range) => {
    const startDate = new Date(
      item?.startDate || subWeeks(new Date(), 1)
    );
    const endDate = new Date(
      item?.endDate || new Date()
    );
    onDateSelect({ startDate, endDate });
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const currentStartDate = React.useMemo(
    () => formatDate(startDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startDate]
  );
  const currentEndDate = React.useMemo(
    () => formatDate(endDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endDate]
  );

  return (
    <Box>
      <Button
        id="basic-button"
        size="small"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        startIcon={
          <Box
            component="img"
            src={calendar}
            alt=""
            sx={{
              width: 18,
              height: 16,
              display: 'block',
              transform: 'scaleX(1.1)',
              transformOrigin: 'center',
            }}
          />
        }
        sx={{
          minHeight: 32,
          maxHeight: 32,
          px: 1.5,
          py: 0,
          fontSize: "0.8125rem",
          fontWeight: 500,
          lineHeight: 1.2,
          border: "1px solid",
          borderColor: "primary.light",
          boxShadow: "none",
          borderRadius: 2,
          "& .MuiButton-startIcon": {
            mr: 0.75,
            ml: 0,
          },
        }}>
        {currentStartDate} - {currentEndDate}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{ mt: "4px" }}>
        {/* @ts-ignore */}
        <MyDateRange
          onChange={item => {
            changeRange(item.range1);
          }}
          moveRangeOnFirstSelection={false}
          months={2}
          ranges={[{ startDate, endDate }]}
          direction="horizontal"
          rangeColors={["var(--mui-palette-primary-main)"]}
          maxDate={others?.maxDate || new Date()}
          showPreview={false}
          staticRanges={allStaticRanges}
          inputRanges={[defaultInputRanges[0]]}
        />
      </Menu>
    </Box>
  );
};

export default DateRangePicker;
